import { createAnswerGroup } from './buildAnswerGroups';
import type { AnswerGroupContext } from './answerGroupSummaries';
import type { AnswerGroup, AnswerGroupRowMode } from './types';

type ParticipantScope = Pick<AnswerGroup, 'directRespondentIds' | 'directResponseIds'>;

/** Snapshot-scoped projections. The table never reconstructs respondents or knows the report schema. */
export function createAnswerGroupRowSelector(context: AnswerGroupContext) {
  const participantCache = new WeakMap<ParticipantScope, AnswerGroup[]>();
  const hierarchyCache = new WeakMap<AnswerGroup, AnswerGroup>();
  const selectionCache = new WeakMap<AnswerGroup[], Map<AnswerGroupRowMode, AnswerGroup[]>>();

  const directParticipants = (group: ParticipantScope): AnswerGroup[] => {
    const cached = participantCache.get(group);
    if (cached) return cached;
    const buckets = new Map<
      string,
      { members: Set<string>; responses: Set<string>; anonymous: boolean; teamId?: string }
    >();
    const memberBucket = new Map<string, string>();
    for (const id of group.directRespondentIds) {
      const teamId = context.teamByRespondent.get(id);
      const anonymous = context.anonymousRespondentIds.has(id);
      const key = JSON.stringify([teamId ?? null, anonymous ? 'anonymous' : id]);
      const bucket = buckets.get(key) ?? { members: new Set(), responses: new Set(), anonymous, teamId };
      bucket.members.add(id);
      buckets.set(key, bucket);
      memberBucket.set(id, key);
    }
    for (const responseId of group.directResponseIds) {
      const response = context.responsesById.get(responseId);
      const key = response ? memberBucket.get(String(response.respondentId)) : undefined;
      if (key) buckets.get(key)!.responses.add(responseId);
    }
    const participants = [...buckets]
      .map(([key, bucket]) => {
        const respondentId = [...bucket.members][0];
        const respondent = context.respondentsById.get(respondentId);
        const label = bucket.anonymous
          ? context.labels.anonymous(bucket.members.size)
          : `${respondent?.firstname ?? ''} ${respondent?.surname ?? ''}`.trim();
        const team = bucket.teamId ? context.teamsById.get(bucket.teamId) : undefined;
        return createAnswerGroup(context, {
          id: `${bucket.anonymous ? 'anonymous' : 'individual'}:${key}`,
          label,
          type: bucket.anonymous ? 'anonymous' : 'individual',
          teamId: bucket.teamId,
          teamNames: [team ? team.teamName || context.labels.withoutSupervisor : context.labels.unassigned],
          respondentId: bucket.anonymous ? undefined : respondentId,
          initials: bucket.anonymous
            ? undefined
            : `${respondent?.firstname.slice(0, 1) ?? ''}${respondent?.surname.slice(0, 1) ?? ''}`,
          directRespondentIds: bucket.members,
          directResponseIds: bucket.responses,
        });
      })
      .sort(
        (a, b) => Number(a.type === 'anonymous') - Number(b.type === 'anonymous') || a.label.localeCompare(b.label),
      );
    participantCache.set(group, participants);
    return participants;
  };

  const withParticipants = (group: AnswerGroup): AnswerGroup => {
    if (group.type === 'individual' || group.type === 'anonymous') return group;
    const cached = hierarchyCache.get(group);
    if (cached) return cached;
    const projected = { ...group, children: [...group.children.map(withParticipants), ...directParticipants(group)] };
    hierarchyCache.set(group, projected);
    return projected;
  };

  return (groups: AnswerGroup[], mode: AnswerGroupRowMode): AnswerGroup[] => {
    if (mode === 'teams') return groups;
    const modes = selectionCache.get(groups) ?? new Map<AnswerGroupRowMode, AnswerGroup[]>();
    const cached = modes.get(mode);
    if (cached) return cached;
    let result: AnswerGroup[];
    if (mode === 'teamsAndParticipants') {
      result = groups.map(withParticipants);
    } else {
      // Merge root membership first: overlapping custom/team roots must not duplicate individuals
      // or silently drop a partial anonymous pool with the same team key.
      const allRespondents = new Set<string>();
      const allResponses = new Set<string>();
      groups.forEach((group) => {
        group.respondentIds.forEach((id) => allRespondents.add(id));
        group.responseIds.forEach((id) => allResponses.add(id));
      });
      result = directParticipants({ directRespondentIds: allRespondents, directResponseIds: allResponses });
    }
    modes.set(mode, result);
    selectionCache.set(groups, modes);
    return result;
  };
}
